import { AxiosRequestConfig } from 'axios';
import { clientPortalApi } from 'src/api';
import { TanstackTableSearchParamsAdapter } from '@itm/shared-frontend/lib/utils';

import {
  UserDetailResponse,
  CurrentUserResponse,
  GetUserAuthEventsResponse,
  GetUserDetailsResponseDto,
  CreateUserModel,
  CheckUserModel,
  UpdateUserModel,
  UserCompanyModel,
  UserSearchPagedResponse,
  AddClientPortalModel,
  CreateSupportUserModel,
  GetUsersModel,
  UserAuditArea,
  UserAuditResponsePagedResponse,
  UserAuditDetailResponse,
} from 'src/types';

export const getUserById = (userId: string, config: AxiosRequestConfig = {}) =>
  clientPortalApi.get<UserDetailResponse>(`/api/v1/users/${userId}`, config);

export type UserAuthEventsByIdSearchParams = TanstackTableSearchParamsAdapter & {
  dateFrom?: string;
  dateTo?: string;
};
export const getUserAuthEventsById = (
  userId: string,
  params: UserAuthEventsByIdSearchParams,
  config: AxiosRequestConfig = {},
) => clientPortalApi.get<GetUserAuthEventsResponse>(`/api/v1/users/${userId}/authEvents`, { ...config, params });
export const getIdentityUserDetailsById = (userId: string, config: AxiosRequestConfig = {}) =>
  clientPortalApi.get<GetUserDetailsResponseDto>(`/api/v1/users/${userId}/userDetails`, config);
export const createUser = (data: CreateUserModel) => clientPortalApi.post<string>('/api/v1/users', data);
export const createSupportUser = (data: CreateSupportUserModel) =>
  clientPortalApi.post<string>('/api/v1/users/support-role', data);
export const createUserCheck = (data: CheckUserModel) =>
  clientPortalApi.post<string>('/api/v1/users/email-check', data);
export const updateUser = (data: UpdateUserModel) => clientPortalApi.patch<void>('/api/v1/users', data);
export const assignUserClientPortalRole = (data: AddClientPortalModel) =>
  clientPortalApi.post<void>('/api/v1/users/assignRole', data);
export const updateUserClientPortalRole = (data: AddClientPortalModel) =>
  clientPortalApi.patch<void>('/api/v1/users/updateUserRole', data);

export const getCurrentUser = () => clientPortalApi.get<CurrentUserResponse>('/api/v1/users/current');
export const activateCurrentUser = () => clientPortalApi.post<void>('/api/v1/users/activateCurrent');

export const getUserListSearch = (data: GetUsersModel, config: AxiosRequestConfig = {}) =>
  clientPortalApi.post<UserSearchPagedResponse>('/api/v1/users/search', data, config);

export const assignUserToCompany = (data: UserCompanyModel) =>
  clientPortalApi.patch<void>('/api/v1/users/addUserToCompany', data);
export const removeUserFromCompany = (data: UserCompanyModel) =>
  clientPortalApi.patch<void>('/api/v1/users/removeUserFromCompany', data);

export const activateUser = (userId: string) => clientPortalApi.post<void>(`/api/v1/users/${userId}/activate`);
export const deactivateUser = (userId: string) => clientPortalApi.post<void>(`/api/v1/users/${userId}/deactivate`);
export const resendInvitationEmail = (userId: string) =>
  clientPortalApi.post<void>(`/api/v1/users/${userId}/resendConfirmationCode`);
export const resetUserMFA = (userId: string) => clientPortalApi.post<void>(`/api/v1/users/${userId}/reset-mfa`);
export const resendTemporaryPassword = (userId: string) =>
  clientPortalApi.post<void>(`/api/v1/users/${userId}/resendTemporaryPassword`);

export const deleteUser = (userId: string) => clientPortalApi.delete<void>(`/api/v1/users/${userId}`);

export type UserAuditSearchParams = TanstackTableSearchParamsAdapter & {
  area?: UserAuditArea;
  dateFrom?: string;
  dateTo?: string;
};

export const getUserAuditSearch = (userId: string, params: UserAuditSearchParams, config: AxiosRequestConfig = {}) =>
  clientPortalApi.get<UserAuditResponsePagedResponse>(`/api/v1/users/${userId}/audits`, {
    ...config,
    params,
  });

export const getUserAuditExport = (
  userId: string,
  params: Omit<UserAuditSearchParams, 'take' | 'skip'>,
  config: AxiosRequestConfig = {},
) =>
  clientPortalApi.get<Blob>(`/api/v1/users/${userId}/audits/export`, {
    ...config,
    params,
    responseType: 'blob',
  });

export const getUserAuditDetailsById = (userId: string, auditId: string, config: AxiosRequestConfig = {}) =>
  clientPortalApi.get<UserAuditDetailResponse>(`/api/v1/users/${userId}/audits/${auditId}`, config);

export const getCompanyUserAuditSearch = (
  companyId: string,
  params: UserAuditSearchParams,
  config: AxiosRequestConfig = {},
) =>
  clientPortalApi.get<UserAuditResponsePagedResponse>(`/api/v1/users/company/${companyId}/audits`, {
    ...config,
    params,
  });

export const getCompanyUserAuditExport = (
  companyId: string,
  params: Omit<UserAuditSearchParams, 'take' | 'skip'>,
  config: AxiosRequestConfig = {},
) =>
  clientPortalApi.get<Blob>(`/api/v1/users/company/${companyId}/audits/export`, {
    ...config,
    params,
    responseType: 'blob',
  });
